// ============================================================================
// mutuellesSortantes.js : une mutuelle qui quitte l'Union (demande de Pierre
// du 22/10/2026). Miroir de functions/lib/mutuellesSortantes.js (parité tenue
// par src/mutuellesSortantes.test.js, table d'alias comprise).
// ============================================================================
// `memberLists/{id}.sortieLe` (« AAAA-MM-JJ », dernier jour d'adhésion),
// réglé par un administrateur d'Enio : pour une séance STRICTEMENT
// postérieure, les représentants de la mutuelle, quels qu'ils soient, ne sont
// ni proposés, ni invités, ni comptés (base du quorum, votants, majorités,
// excusés du PV). Jusqu'à cette date incluse, rien ne change.
//
// Mutuelle d'une personne = celle qu'elle REPRÉSENTE dans l'instance (celle de
// gouvernance pour un mandat statutaire, AG ou CA avec le rôle
// d'administrateur ; sinon celle de sa fiche ou de sa ligne), résolue par la table d'alias
// (la même que le serveur, lib/lfPortefeuillesMutuelles.js), jamais par le
// seul nom d'une liste. Fiche : delegateId, identifiant de ligne égal à une
// fiche, puis l'adresse ; jamais le seul nom.
// ============================================================================

/** Groupes d'alias : copie EXACTE de functions/lib/lfPortefeuillesMutuelles.js
 *  (le test de parité la compare). Aucun nom de personne. */
export const GROUPES = Object.freeze([
  { cle: "amellis", noms: ["Amellis", "Amellis Mutuelles", "Amellis Mutuelle", "Amelis"] },
  { cle: "carel", noms: ["Carel Mutuelle", "Carel"] },
  { cle: "cmam", noms: ["CMAM", "C.M.A.M"] },
  { cle: "integrance", noms: ["Intégrance"] },
  { cle: "interiale", noms: ["Intériale", "Groupe Intériale"] },
  { cle: "mgps", noms: ["MGPS", "M.G.P.S"] },
  { cle: "mutami", noms: ["Mutami", "Mutami Mutuelle"] },
  { cle: "mutuale", noms: ["Mutuale", "Mutuale Mutuelle"] },
  { cle: "mutuelle de la corse", noms: ["Mutuelle de la Corse", "MDC", "M.D.C", "Mutuelle Corse"] },
  { cle: "smh", noms: ["SMH", "S.M.H", "SMH Hauts-de-France", "SMH Haut-de-France"] },
  { cle: "tutelaire", noms: ["Tutélaire"] },
  { cle: "umcl", noms: ["UMCL", "U.M.C.L"] },
  { cle: "umgp", noms: ["UMGP", "MEP", "SMEREP", "HEYME", "Heyme Care", "MEP (UMGP)", "SMEREP (UMGP)"] },
]);

const JOUR = /^\d{4}-\d{2}-\d{2}$/;
const norm = (s) => String(s == null ? "" : s).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
/** Forme de comparaison (celle du serveur) : ponctuation lue comme une espace. */
export const normaliser = (s) => norm(s).replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim();
const ALIAS = (() => {
  const m = new Map();
  for (const g of GROUPES) for (const n of [g.cle, ...g.noms]) m.set(normaliser(n), g.cle);
  return m;
})();
const estEnio = (s) => /(^| )enio( |$)/.test(normaliser(s));
/** La clé d'un nom de mutuelle (null : vide, ou l'équipe d'Enio). */
export function cleAlias(nom) {
  const n = normaliser(nom);
  if (!n || estEnio(n)) return null;
  return ALIAS.get(n) || n;
}
const normEmail = (e) => String(e || "").normalize("NFC").toLowerCase().replace(/\s+/g, "").trim();

/** Map(clé d'alias → sortieLe), depuis les listes de mutuelles. */
export function sortiesDes(listes) {
  const m = new Map();
  for (const l of Array.isArray(listes) ? listes : []) {
    const s = String((l && l.sortieLe) || "");
    if (!JOUR.test(s)) continue;
    for (const nom of [l.name, l.id, l.abreviation]) {
      const k = cleAlias(nom);
      if (k) m.set(k, s);
    }
  }
  return m;
}

/** Sortie à cette date (STRICTEMENT après le dernier jour d'adhésion) ? */
export function mutuelleSortieA(nom, dateISO, sorties) {
  if (!JOUR.test(String(dateISO || "")) || !sorties || !sorties.size) return false;
  const k = cleAlias(nom);
  const s = k ? sorties.get(k) : null;
  return !!s && String(dateISO) > s;
}

/** La fiche d'une ligne de séance (jamais par le seul nom). */
export function ficheDe(p, delegates, { idEstFiche = true } = {}) {
  const liste = Array.isArray(delegates) ? delegates : [];
  if (!p) return null;
  if (p.delegateId) {
    const f = liste.find((d) => d && d.id === p.delegateId);
    if (f) return f;
  }
  // Une ligne de RÉUNION d'Activité porte un identifiant de ligne, jamais une
  // identité (idEstFiche: false).
  if (p.id && idEstFiche) {
    const f = liste.find((d) => d && d.id === p.id);
    if (f) return f;
  }
  const m = normEmail(p.email);
  return m ? liste.find((d) => d && normEmail(d.email) === m) || null : null;
}

const TYPES = { AG: "AG", CA: "CA", CD: "CD", CODIR: "CD", BU: "BU", BUREAU: "BU" };
export const typeCanon = (t) => { const v = String(t || "").trim().toUpperCase(); return TYPES[v] || v; };
/** La mutuelle qu'une personne REPRÉSENTE dans une instance (décision de
 *  Pierre du 22/10/2026, Ludivine MAUDET : salariée de l'UMGP, déléguée de
 *  Mutami à l'AG, membre de droit du CODIR comme coordinatrice d'un domaine).
 *  La mutuelle de gouvernance ne vaut que pour un mandat STATUTAIRE : à l'AG
 *  (les délégués), au CA pour qui a le rôle d'administrateur. Ailleurs (CA
 *  sans mandat, CODIR, Bureau, invités), on siège à son propre titre : la
 *  mutuelle de sa fiche (employeur), sinon celle de sa ligne. Type inconnu :
 *  la représentation (la règle la plus large). */
export function estStatutaire(fiche, instanceType) {
  const t = typeCanon(instanceType);
  const roles = Array.isArray(fiche && fiche.roles) ? fiche.roles : [];
  return !t || t === "AG" || (t === "CA" && roles.includes("CA"));
}
export function mutuelleRepresentee(p, fiche, instanceType) {
  if (estStatutaire(fiche, instanceType)) return (fiche && fiche.mutuelleGouvernance) || (p && p.mutuelle) || (fiche && fiche.mutuelle) || "";
  return (fiche && fiche.mutuelle) || (p && p.mutuelle) || "";
}

/** Un AUTRE TITRE pour siéger quand la qualité de délégué(e) d'une mutuelle
 *  sortie tombe (décision de Pierre du 23/10/2026) : membre du CODIR à son
 *  propre titre (mutuelle employeuse non sortie), ou salarié(e) d'Enio. */
export function autreTitre(fiche, dateISO, sorties) {
  if (!fiche) return false;
  if (estEnio(fiche.mutuelle)) return true;
  const roles = Array.isArray(fiche.roles) ? fiche.roles : [];
  return roles.includes("CD") && !!cleAlias(fiche.mutuelle) && !mutuelleSortieA(fiche.mutuelle, dateISO, sorties);
}
/** « exercice » | « sansVoix » (invitée à un autre titre, jamais votante) | « sorti ». */
export function statutSortie(p, fiche, dateISO, sorties, instanceType = "") {
  if (!mutuelleSortieA(mutuelleRepresentee(p, fiche, instanceType), dateISO, sorties)) return "exercice";
  return estStatutaire(fiche, instanceType) && autreTitre(fiche, dateISO, sorties) ? "sansVoix" : "sorti";
}
const statutDe = (p, dateISO, { delegates = [], sorties, instanceType = "", idEstFiche = true } = {}) =>
  (p ? statutSortie(p, ficheDe(p, delegates, { idEstFiche }), dateISO, sorties, instanceType) : "exercice");
export function participantSorti(p, dateISO, ctx = {}) { return statutDe(p, dateISO, ctx) === "sorti"; }
export function participantSansVoix(p, dateISO, ctx = {}) { return statutDe(p, dateISO, ctx) === "sansVoix"; }

export const ficheSortie = (fiche, dateISO, sorties, instanceType = "") => !!fiche && mutuelleSortieA(mutuelleRepresentee(null, fiche, instanceType), dateISO, sorties);

export function partager(participants, dateISO, ctx = {}) {
  const gardes = [];
  const sortis = [];
  const sansVoix = [];   // restent dans `gardes` (invités à un autre titre)
  for (const p of Array.isArray(participants) ? participants : []) {
    const st = p ? statutDe(p, dateISO, ctx) : "exercice";
    if (st === "sorti") sortis.push(p);
    else { gardes.push(p); if (st === "sansVoix") sansVoix.push(p); }
  }
  return { gardes, sortis, sansVoix };
}

export function dateDeSeance(sess) {
  const iso = String((sess && sess.dateISO) || "");
  if (JOUR.test(iso)) return iso;
  const d = String((sess && sess.date) || "");
  if (JOUR.test(d)) return d;
  const m = d.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
}

export function sortieValide(s) {
  const v = String(s || "").trim();
  if (!v) return { ok: true, valeur: "" };
  if (!JOUR.test(v)) return { ok: false };
  const d = new Date(`${v}T12:00:00Z`);
  return Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== v ? { ok: false } : { ok: true, valeur: v };
}

// ── Ce qu'une séance voit (écran) ───────────────────────────────────────────
/**
 * Le contexte d'une séance pour les calculs de quorum, de votants et de PV :
 * les fiches en exercice à sa date (base du quorum : sans les mandats d'une
 * mutuelle sortie) et ses participants sans ceux d'une mutuelle sortie (ni
 * votants, ni excusés). Rend les tableaux d'ORIGINE quand rien ne sort
 * (mémoïsation des écrans), et `sortis` pour le dire.
 */
export function contexteSeance({ sess, dateISO, instanceType, participants = [], delegates = [], lists = [] } = {}) {
  const date = dateISO || dateDeSeance(sess);
  const type = instanceType || (sess && sess.instanceType) || "";
  const sorties = sortiesDes(lists);
  if (!sorties.size || !JOUR.test(date)) return { participants, delegates, sortis: [], sorties, dateISO: date };
  const { gardes, sortis } = partager(participants, date, { delegates, sorties, instanceType: type });
  const enExercice = (delegates || []).filter((d) => !ficheSortie(d, date, sorties, type));
  return {
    participants: sortis.length ? gardes : participants,
    delegates: enExercice.length === (delegates || []).length ? delegates : enExercice,
    sortis, sorties, dateISO: date,
  };
}

/** Les mutuelles (noms) qui siègent encore à la date d'une séance : sans
 *  celles qui sont sorties de l'Union (ni place, ni carence de désignation). */
export function mutuellesEnExercice(noms, sessOuDate, lists) {
  const date = typeof sessOuDate === "string" ? sessOuDate : dateDeSeance(sessOuDate);
  const sorties = sortiesDes(lists);
  if (!sorties.size) return noms || [];
  return (noms || []).filter((n) => !mutuelleSortieA(n, date, sorties));
}

/** Le libellé d'une sortie, pour l'écran : « Mutami, sortie le 31/12/2026 ». */
export function libelleSortie(nom, sorties) {
  const s = sorties && sorties.get(cleAlias(nom));
  if (!s) return "";
  const [a, m, j] = s.split("-");
  return `sortie de l'Union le ${j}/${m}/${a}`;
}

/** Les fiches en exercice à la date d'une séance (séance ou date ISO) : sans
 *  celles d'une mutuelle sortie. À passer AUX CALCULS (base du quorum,
 *  votants, voix, PV) à la place de l'annuaire, jamais à un écran qui
 *  enregistre : la liste des participants, elle, n'est jamais filtrée en
 *  silence. Rend l'annuaire d'origine quand personne ne sort. */
// Référence STABLE pour les mêmes entrées (annuaire, date, listes) : un
// écran qui en dépend (useMemo, useEffect) ne recalcule pas à chaque rendu.
const memo = new WeakMap();
export function delegatesPourSeance(delegates, sessOuDate, lists) {
  const date = typeof sessOuDate === "string" ? sessOuDate : dateDeSeance(sessOuDate);
  const type = typeof sessOuDate === "string" ? "" : String((sessOuDate && sessOuDate.instanceType) || "");
  const d = Array.isArray(delegates) ? delegates : [];
  const l = Array.isArray(lists) ? lists : [];
  const prec = memo.get(d);
  if (prec && prec.date === date && prec.type === type && prec.lists === l) return prec.resultat;
  const resultat = contexteSeance({ dateISO: date, instanceType: type, delegates: d, lists: l }).delegates;
  memo.set(d, { date, type, lists: l, resultat });
  return resultat;
}

// ── Réunions d'Activité (décision de Pierre du 23/10/2026) ──────────────────
// Miroir de functions/lib/mutuellesSortantes.js (typeDeReunion,
// invitesDeReunion) et de l'intitulé reconnu par la liaison
// (functions/lib/liaisonReunion.js, typeDuTitre).
const REPERES_TITRE = [
  ["AG", [/(^|[^\p{L}\d])AG[EO]?(?![\p{L}\d])/u, /assembl[ée]e\s+g[ée]n[ée]rale/iu]],
  ["CA", [/(^|[^\p{L}\d])CA(?![\p{L}\d])/u, /conseil\s+d['’]\s*administration/iu]],
  ["CD", [/(^|[^\p{L}\d])co-?dir(?![\p{L}\d])/iu, /comit[ée]\s+de\s+direction/iu]],
  ["BU", [/(^|[^\p{L}\d])bureau(?![\p{L}\d])/iu]],
];
export function typeDuTitre(titre) {
  const t = String(titre || "");
  const trouves = REPERES_TITRE.filter(([, res]) => res.some((re) => re.test(t))).map(([k]) => k);
  return trouves.length === 1 ? trouves[0] : null;
}
const TYPES_INSTANCE = ["AG", "CA", "CD", "BU"];
/** Le type d'instance d'une réunion : porté, sinon reconnu par l'intitulé. */
export function typeDeReunion(reunion) {
  const t = typeCanon(reunion && reunion.instanceType);
  if (TYPES_INSTANCE.includes(t)) return t;
  const parTitre = typeDuTitre(reunion && reunion.titre);
  return parTitre && TYPES_INSTANCE.includes(parTitre) ? parTitre : null;
}
/** Typée instance : les représentants d'une mutuelle sortie sont RETIRÉS ;
 *  autre réunion : seulement SIGNALÉS. */
export function invitesDeReunion(reunion, { delegates = [], sorties } = {}) {
  const r = reunion || {};
  const date = String(r.dateISO || "").slice(0, 10);
  const type = typeDeReunion(r);
  const lignes = (Array.isArray(r.invites) ? r.invites : []).filter(Boolean);
  if (!sorties || !sorties.size || !JOUR.test(date)) return { type, gardes: lignes, retires: [], avertis: [] };
  const ctx = { delegates, sorties, idEstFiche: false };
  if (type) {
    const { gardes, sortis, sansVoix } = partager(lignes, date, { ...ctx, instanceType: type });
    return { type, gardes, retires: sortis, avertis: [], sansVoix };
  }
  return { type: null, gardes: lignes, retires: [], avertis: partager(lignes, date, ctx).sortis };
}
